/**
 * Bomba Cart Drawer - Functional Logic
 * Uses GSAP for animations and Shopify AJAX API for cart updates.
 */

class BombaCartDrawer {
  constructor() {
    this.drawer = document.getElementById('BombaCartDrawer');
    if (!this.drawer) return;

    this.panel = document.getElementById('js-cart-panel');
    this.overlay = document.getElementById('js-cart-overlay');
    this.closeBtn = document.getElementById('js-cart-close');
    this.cartOpenBtns = document.querySelectorAll('#js-cart-open, .js-cart-open');
    this.itemsContainer = document.getElementById('js-cart-items');
    this.contentContainer = document.querySelector('.bomba-cart-drawer__content');

    this.isOpen = false;
    this.updateTimers = new Map();
    this.init();
  }

  init() {
    // Move to <body> so it escapes any section stacking context (same fix as sliding menu)
    if (this.drawer && this.drawer.parentElement !== document.body) {
      document.body.appendChild(this.drawer);
    }

    // Lift the overlay out of the drawer container onto its own <body>-level
    // layer (z 9990, below the header at 9995). Inside the container — which we
    // raise above the header so the panel can slide over it — the overlay would
    // inherit that higher stacking and dim the header. As a sibling of <body> it
    // sits under the header, so the header stays fully visible (like the menu).
    if (this.overlay && this.overlay.parentElement !== document.body) {
      document.body.appendChild(this.overlay);
    }

    // Open listeners
    this.cartOpenBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close listeners
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());

    // Close when clicking anywhere outside the panel (the blurred page).
    // Works regardless of which element receives the click: the blurred
    // content is pointer-events:none, so the event still bubbles to document.
    document.addEventListener('click', (e) => {
      if (!this.isOpen) return;
      if (this.panel && this.panel.contains(e.target)) return;
      if (e.target.closest('#js-cart-open, .js-cart-open')) return;
      this.close();
    });

    // Delegate item actions (quantity, remove)
    if (this.drawer) {
      this.drawer.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('.bomba-cart-item__remove');
        if (removeBtn) {
          this.updateItem(removeBtn.dataset.id, 0);
          return;
        }

        const qtyBtn = e.target.closest('.bomba-quantity__btn');
        if (qtyBtn) {
          const id = qtyBtn.dataset.id;
          const input = qtyBtn.parentElement.querySelector('.bomba-quantity__input');
          let currentQty = parseInt(input.value);

          if (qtyBtn.dataset.action === 'increase') {
            const newQty = currentQty + 1;
            input.value = newQty;
            this.optimisticUpdate(id, newQty);
            this.debouncedUpdate(id, newQty);
          } else if (qtyBtn.dataset.action === 'decrease' && currentQty > 0) {
            const newQty = currentQty - 1;
            input.value = newQty;
            this.optimisticUpdate(id, newQty);
            this.debouncedUpdate(id, newQty);
          }
        }
      });
    }

    // Escape key
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
    
    // Listen for Shopify "Added to Cart" events if global.js or cart.js triggers them
    document.addEventListener('cart:updated', () => this.refresh());
    // Adding an item shows a small pop near the cart icon instead of
    // opening the whole drawer.
    document.addEventListener('cart:item-added', (e) => {
      const qty = e && e.detail && e.detail.quantity ? e.detail.quantity : 1;
      this.notifyAdded(qty);
    });
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    // Blur the page content behind the drawer
    document.body.classList.add('bomba-overlay-open');

    // No scroll-locking (matches inspiration repo to prevent layout shift)
    // document.documentElement.classList.add('cart-open');



    // Animation
    gsap.set(this.drawer, { pointerEvents: 'auto' });
    // Overlay must capture clicks so tapping next to the panel closes the cart
    gsap.set(this.overlay, { pointerEvents: 'auto' });
    gsap.to(this.overlay, { opacity: 1, duration: 0.3 });
    gsap.to(this.panel, { x: '0%', duration: 0.5, ease: 'power4.out' });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Remove the page-content blur
    document.body.classList.remove('bomba-overlay-open');

    // Restore scroll and layout shift (if we had locked it)
    // document.documentElement.classList.remove('cart-open');
    
    
    
    // Animation
    gsap.to(this.overlay, { opacity: 0, duration: 0.3 });
    gsap.to(this.panel, {
      x: '100%',
      duration: 0.4,
      ease: 'power4.in',
      onComplete: () => gsap.set([this.drawer, this.overlay], { pointerEvents: 'none' })
    });
  }

  // Instant DOM update from cached unit price — runs before the API call
  optimisticUpdate(id, newQty) {
    const row = this.drawer.querySelector(`.bomba-cart-item[data-id="${id}"]`);
    if (!row) return;
    const unitPrice = parseInt(row.dataset.unitPrice, 10);
    if (!unitPrice) return;

    const priceEl = row.querySelector('.bomba-cart-item__price');
    if (priceEl) priceEl.textContent = this.formatMoney(unitPrice * newQty);

    let total = 0;
    let totalItems = 0;
    this.drawer.querySelectorAll('.bomba-cart-item').forEach(item => {
      const itemId = item.dataset.id;
      const itemUnitPrice = parseInt(item.dataset.unitPrice, 10) || 0;
      const itemInput = item.querySelector('.bomba-quantity__input');
      const itemQty = itemId === id ? newQty : parseInt(itemInput ? itemInput.value : 0, 10);
      total += itemUnitPrice * itemQty;
      totalItems += itemQty;
    });

    const totalEl = this.drawer.querySelector('.bomba-cart-total span:last-child');
    if (totalEl) totalEl.textContent = this.formatMoney(total);
    this.updateBadge(totalItems);
    this.updateFreeShipping(total);
  }

  // Free-shipping progress meter (threshold from data-attr, in cents)
  updateFreeShipping(totalCents) {
    const el = this.drawer.querySelector('#js-cart-ship');
    if (!el) return;
    const threshold = parseInt(el.dataset.threshold, 10) || 7500;
    const remaining = threshold - totalCents;
    const pct = Math.max(0, Math.min(100, Math.round((totalCents / threshold) * 100)));

    const fill = el.querySelector('#js-cart-ship-fill');
    if (fill) fill.style.width = pct + '%';
    const track = el.querySelector('.bomba-cart-ship__track');
    if (track) track.setAttribute('aria-valuenow', pct);

    const msg = el.querySelector('#js-cart-ship-msg');
    if (!msg) return;
    msg.textContent = '';
    if (remaining <= 0) {
      el.classList.add('is-reached');
      const icon = document.createElement('span');
      icon.className = 'material-symbols-outlined';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = 'check_circle';
      const strong = document.createElement('strong');
      strong.textContent = 'gratis verzending';
      msg.append(icon, 'Je hebt ', strong, '!');
    } else {
      el.classList.remove('is-reached');
      const strong = document.createElement('strong');
      strong.textContent = this.formatMoney(remaining);
      msg.append('Nog ', strong, ' tot gratis verzending');
    }
  }

  debouncedUpdate(id, qty) {
    if (this.updateTimers.has(id)) clearTimeout(this.updateTimers.get(id));
    this.updateTimers.set(id, setTimeout(() => {
      this.updateTimers.delete(id);
      this.updateItem(id, qty);
    }, 300));
  }

  // Fires after debounce — silently syncs with server, no UI blocking
  async updateItem(id, qty) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity: qty })
      });

      if (!response.ok) {
        await this.refresh();
        return;
      }

      const cart = await response.json();

      if (qty === 0) {
        const row = this.drawer.querySelector(`.bomba-cart-item[data-id="${id}"]`);
        if (row) row.remove();
        if (cart.item_count === 0) {
          await this.refresh();
          return;
        }
      }

      // Correct any price discrepancies (e.g. quantity-based discounts)
      cart.items.forEach(item => {
        const row = this.drawer.querySelector(`.bomba-cart-item[data-id="${item.key}"]`);
        if (!row) return;
        const priceEl = row.querySelector('.bomba-cart-item__price');
        if (priceEl) priceEl.textContent = this.formatMoney(item.final_line_price);
      });

      const totalEl = this.drawer.querySelector('.bomba-cart-total span:last-child');
      if (totalEl) totalEl.textContent = this.formatMoney(cart.total_price);
      this.updateBadge(cart.item_count);
      this.updateFreeShipping(cart.total_price);

    } catch (error) {
      console.error('Error updating cart:', error);
      await this.refresh();
    }
  }

  formatMoney(cents) {
    const format = (window.Shopify && window.Shopify.moneyFormat) || '€{{amount_with_comma_separator}}';
    const value = cents / 100;
    const withDot = value.toFixed(2);
    const withComma = withDot.replace('.', ',');
    const noDecimals = Math.floor(value).toString();
    return format
      .replace('{{amount_with_comma_separator}}', withComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', noDecimals)
      .replace('{{amount_no_decimals}}', noDecimals)
      .replace('{{amount}}', withDot);
  }

  updateBadge(itemCount) {
    const btn = document.getElementById('js-cart-open');
    if (!btn) return;
    let badge = btn.querySelector('.bomba-header__cart-count');
    if (itemCount > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'bomba-header__cart-count';
        btn.appendChild(badge);
      }
      badge.textContent = itemCount;
    } else if (badge) {
      badge.remove();
    }
  }

  async refresh() {
    try {
      // Get the updated section markup
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const data = await response.json();
      const html = data['cart-drawer'];
      
      if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.getElementById('BombaCartDrawer');
        
        if (newDrawer) {
          // Update the content of our drawer with the new markup
          // We only want to update the panel content to maintain open state
          const newContent = newDrawer.querySelector('.bomba-cart-drawer__content').innerHTML;
          const newFooter = newDrawer.querySelector('.bomba-cart-drawer__footer');
          const newHeader = newDrawer.querySelector('.bomba-cart-drawer__header').innerHTML;
          
          if (this.contentContainer) this.contentContainer.innerHTML = newContent;
          if (newHeader) document.querySelector('.bomba-cart-drawer__header').innerHTML = newHeader;
          
          // Re-bind close button because header was overwritten
          this.closeBtn = document.getElementById('js-cart-close');
          if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

          const footer = document.querySelector('.bomba-cart-drawer__footer');
          if (newFooter) {
            if (footer) {
              footer.innerHTML = newFooter.innerHTML;
            } else {
              this.panel.appendChild(newFooter);
            }
          } else if (footer) {
            footer.remove();
          }
          
          this.updateBubbleCount();
        }
      }
    } catch (error) {
      console.error('Error refreshing cart drawer:', error);
    }
  }

  // Only used after a full section refresh (add-to-cart, empty cart state)
  updateBubbleCount() {
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => this.updateBadge(cart.item_count));
  }

  // Add-to-cart feedback WITHOUT opening the drawer: refresh the drawer
  // contents silently (so it's current when the user opens it later), bump
  // the header badge, and float a small "+N" pop next to the cart icon.
  notifyAdded(quantity) {
    const qty = quantity && quantity > 0 ? quantity : 1;
    this.showAddedPop(qty);
    // Refresh drawer markup in the background so a later open() is up to date.
    this.refresh();
    // Reliable badge update + bounce once the server count is known.
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        this.updateBadge(cart.item_count);
        this.bounceBadge();
      })
      .catch(() => {});
  }

  // Floating "+N item(s)" bubble anchored just under the header cart icon.
  showAddedPop(quantity) {
    const anchor = document.getElementById('js-cart-open');
    if (!anchor) return;

    const pop = document.createElement('div');
    pop.className = 'bomba-cart-pop';
    pop.setAttribute('role', 'status');

    const icon = document.createElement('span');
    icon.className = 'material-symbols-outlined';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = 'add_shopping_cart';

    const label = document.createElement('span');
    label.textContent = `+${quantity} ${quantity === 1 ? 'item' : 'items'}`;

    pop.append(icon, label);

    const rect = anchor.getBoundingClientRect();
    pop.style.left = `${rect.left + rect.width / 2}px`;
    pop.style.top = `${rect.bottom + 10}px`;

    pop.addEventListener('animationend', () => pop.remove());
    document.body.appendChild(pop);
  }

  // Bottom-centre alert toast when an add-to-cart fails, so a tap that can't
  // complete (sold out, no connection) is never silent. Lives in the thumb
  // zone and lingers long enough to read. Distinct from the success pop.
  notifyError(message) {
    const text = message && message.trim()
      ? message.trim()
      : 'Kon niet toevoegen. Probeer het opnieuw.';

    // Never stack duplicates: replace any toast already on screen.
    const existing = document.querySelector('.bomba-cart-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'bomba-cart-toast';
    toast.setAttribute('role', 'alert');

    const icon = document.createElement('span');
    icon.className = 'material-symbols-outlined';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = 'error';

    const label = document.createElement('span');
    label.textContent = text;

    toast.append(icon, label);
    toast.addEventListener('animationend', () => toast.remove());
    document.body.appendChild(toast);
  }

  // Quick pop animation on the header cart count badge.
  bounceBadge() {
    const badge = document.querySelector('#js-cart-open .bomba-header__cart-count');
    if (!badge) return;
    badge.classList.remove('bomba-cart-count--bounce');
    void badge.offsetWidth; // restart the animation
    badge.classList.add('bomba-cart-count--bounce');
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  window.bombaCartDrawer = new BombaCartDrawer();

  // AJAX add-to-cart for product cards (collections page etc.)
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.bomba-product-card__add-to-cart[data-variant-id]');
    if (!btn || btn.disabled) return;

    const variantId = btn.dataset.variantId;
    if (!variantId || variantId === '0') return;

    const icon = btn.querySelector('.material-symbols-outlined');
    const originalIconText = icon ? icon.textContent : '';
    const textNode = btn.lastChild && btn.lastChild.nodeType === Node.TEXT_NODE ? btn.lastChild : null;
    const originalText = textNode ? textNode.textContent : '';

    btn.disabled = true;
    if (icon) icon.textContent = 'hourglass_empty';
    if (textNode) textNode.textContent = ' ...';

    const reset = () => {
      if (icon) icon.textContent = originalIconText;
      if (textNode) textNode.textContent = originalText;
      btn.disabled = false;
    };

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (res.ok) {
        window.bombaCartDrawer.notifyAdded(1);
        if (icon) icon.textContent = 'check';
        if (textNode) textNode.textContent = ' TOEGEVOEGD';
        setTimeout(reset, 1500);
      } else {
        // Surface why it failed (Shopify's localized reason: sold out, limit
        // reached) instead of silently resetting the button.
        let reason = '';
        try {
          const data = await res.json();
          reason = data && data.description ? data.description : '';
        } catch {}
        if (window.bombaCartDrawer) window.bombaCartDrawer.notifyError(reason);
        if (icon) icon.textContent = 'error';
        setTimeout(reset, 1500);
      }
    } catch {
      if (window.bombaCartDrawer) {
        window.bombaCartDrawer.notifyError('Geen verbinding. Probeer het later opnieuw.');
      }
      if (icon) icon.textContent = 'error';
      setTimeout(reset, 1500);
    }
  });
});
